/**
 * Which seller services (Tillval & tjänster) apply to what is being bought,
 * what each one costs for this order, and the miljöavgift on new tyres
 * (P25009-484).
 *
 * Before this, checkout offered every active service a seller had on every
 * purchase: a buyer of winter tyres was offered "Montering nya hjul", "Svarta
 * bultar" and TPMS, and a buyer of complete wheels was offered omläggning.
 *
 * THE MODEL
 *
 *   appliesTo  where a service shows: product type (tire / rim / wheel, where
 *              "wheel" is a complete wheel) × condition (new / used), stored on
 *              the service as keys like 'tire:used' (BR-1, BR-2). A Bygg paket
 *              package counts as a complete wheel. A service saved without the
 *              setting uses its template's default — no data migration.
 *   priceUnit  what the seller's price covers (BR-7), worked out here for the
 *              server and the browser alike:
 *                'piece' per styck — price × every item it applies to
 *                'set4'  per sats (4 st) — price × items / 4, rounded to whole
 *                        kronor, so two wheels at 399 kr pay 200 kr
 *                'order' fast pris per order — price once
 *              A service saved without a unit uses its template's default too.
 *
 * MONTERING ON NEW COMPLETE WHEELS (BR-5) is not a choice: when the seller has
 * montering set to show there, it is added, ticked and locked. servicesOnOrder
 * puts it on every such order, whatever the browser sent, and the server
 * charges what servicesOnOrder returns. A seller with no active montering
 * service still sells the wheel — nothing is added and nothing is blocked.
 *
 * ONE HELPER FOR EVERY SURFACE (BR-3): the server prices with these functions,
 * and checkout, the listing page and the search cards decide what to show
 * with the same ones. A service a surface does not show is a service the
 * server will not charge.
 *
 * THE COUNT A SERVICE IS PRICED ON is wheel positions, not items. A tyre and a
 * rim become ONE wheel: an order of four tyres and four rims is four
 * omläggningar, not eight. So the count is complete wheels + the larger of
 * tyres and rims, among the order lines the service applies to.
 *
 * WHAT A CALLER PASSES: order lines as { publicData, metadata, physicalItems }.
 * physicalItems is the line's physical item count (a set listing sells one
 * unit that is four wheels), which each runtime works out with its own
 * salesUnit module and freightCore's physicalItemCount — the browser and the
 * server cannot share salesUnit.js.
 *
 * CommonJS, like freightCore.js: the untranspiled API server requires this
 * file and the browser bundles it.
 *
 * MONEY UNIT CONTRACT: a service price is stored in kronor (major units), as
 * the Tjänster page saves it; every amount this module returns is in öre.
 */

const { resolveProductKind } = require('./freightCore');

const PRODUCT_KINDS = ['tire', 'rim', 'wheel'];
const CONDITIONS = ['new', 'used'];

const PRODUCT_KIND_LABELS = { tire: 'Däck', rim: 'Fälgar', wheel: 'Kompletta hjul' };
const CONDITION_LABELS = { new: 'Nya', used: 'Begagnade' };

const targetKey = (kind, condition) => `${kind}:${condition}`;

/** Every product type × condition, in the order the "Visas för" grid shows them. */
const ALL_TARGETS = PRODUCT_KINDS.reduce(
  (keys, kind) => keys.concat(CONDITIONS.map(condition => targetKey(kind, condition))),
  []
);

const both = kind => CONDITIONS.map(condition => targetKey(kind, condition));

const PRICE_UNIT_PIECE = 'piece';
const PRICE_UNIT_SET4 = 'set4';
const PRICE_UNIT_ORDER = 'order';
const PRICE_UNITS = [PRICE_UNIT_PIECE, PRICE_UNIT_SET4, PRICE_UNIT_ORDER];

const PRICE_UNIT_LABELS = {
  [PRICE_UNIT_PIECE]: 'Per styck',
  [PRICE_UNIT_SET4]: 'Per sats (4 st)',
  [PRICE_UNIT_ORDER]: 'Fast pris per order',
};

/* How many items the seller's "per sats" price covers. */
const SET_SIZE = 4;

/**
 * The ready-made services a seller picks from on the Tjänster page. Keep the
 * keys stable: they are stored on every service, and checkout reads them.
 *
 * `defaultAppliesTo` and `defaultPriceUnit` are what a new service starts
 * with in the modal, and what a stored service without its own setting uses.
 * The defaults are the client's spec, §2 and §7
 * (docs/service-targeting-spec-P25009-484.md):
 *   montering    new complete wheels (and packages) only — added and locked
 *                there (BR-5); used complete wheels get no fitting
 *   omläggning   tyres and loose rims: moving tyres onto rims
 *   däckskifte   complete wheels: swapping them on the car
 *   däckhotell   tyres and complete wheels, not loose rims
 *   fälgar       NO default: the seller ticks the boxes they need, so a
 *                Fälgar service saved without a grid shows nowhere
 *   TPMS, bultar, centreringsringar   sit in or on a rim
 * requiresBooking: the workshop jobs need a time slot, the parts don't; the
 * seller can override it per service.
 */
const SERVICE_TEMPLATES = [
  {
    key: 'montering',
    label: 'Montering',
    requiresBooking: true,
    defaultDuration: 30,
    defaultAppliesTo: [targetKey('wheel', 'new')],
    defaultPriceUnit: PRICE_UNIT_SET4,
  },
  {
    key: 'omlaggning',
    label: 'Omläggning / hjulskifte',
    requiresBooking: true,
    defaultDuration: 30,
    defaultAppliesTo: [...both('tire'), ...both('rim')],
    defaultPriceUnit: PRICE_UNIT_SET4,
  },
  {
    key: 'dackskifte',
    label: 'Däckskifte',
    requiresBooking: true,
    defaultDuration: 30,
    defaultAppliesTo: both('wheel'),
    defaultPriceUnit: PRICE_UNIT_SET4,
  },
  {
    key: 'dackhotell',
    label: 'Däckhotell',
    requiresBooking: true,
    defaultDuration: 15,
    defaultAppliesTo: [...both('tire'), ...both('wheel')],
    defaultPriceUnit: PRICE_UNIT_ORDER,
  },
  {
    key: 'falgar',
    label: 'Fälgar',
    requiresBooking: false,
    defaultDuration: 0,
    defaultAppliesTo: [],
    defaultPriceUnit: PRICE_UNIT_PIECE,
  },
  {
    key: 'tpms',
    label: 'TPMS-sensorer',
    requiresBooking: false,
    defaultDuration: 0,
    defaultAppliesTo: [...both('rim'), ...both('wheel')],
    defaultPriceUnit: PRICE_UNIT_SET4,
  },
  {
    key: 'bultar',
    label: 'Svarta bultar',
    requiresBooking: false,
    defaultDuration: 0,
    defaultAppliesTo: [...both('rim'), ...both('wheel')],
    defaultPriceUnit: PRICE_UNIT_SET4,
  },
  /* P25009-403 BR-2 names centering rings as one of the add-ons that belong to
   * a wheel package. A rim whose centre bore is wider than the car's hub needs
   * them to sit true. */
  {
    key: 'centreringsringar',
    label: 'Centreringsringar',
    requiresBooking: false,
    defaultDuration: 0,
    defaultAppliesTo: [...both('rim'), ...both('wheel')],
    defaultPriceUnit: PRICE_UNIT_SET4,
  },
];

const MONTERING_TEMPLATE_KEY = 'montering';

/* What the buyer reads on the locked montering row (BR-5). */
const MONTERING_INCLUDED_TEXT = 'Ingår – hjulen levereras monterade och balanserade';

const getServiceTemplate = key => SERVICE_TEMPLATES.find(t => t.key === key) || null;

const getServiceTemplateLabel = key => getServiceTemplate(key)?.label || key || '';

/**
 * The listing's product type and condition, or null for anything that is not
 * a tyre, rim or complete wheel. "new" only when the listing says so; any
 * other value is used. A Bygg paket package is a complete wheel, and new when
 * it states no condition (the publisher always writes 'new').
 */
const listingTarget = (publicData = {}) => {
  const pd = publicData || {};
  const isPackage = pd.isReachShell === true;
  const kind = isPackage ? 'wheel' : resolveProductKind(pd);
  if (!kind) return null;
  const condition =
    pd.condition === 'new' || (isPackage && !pd.condition) ? 'new' : 'used';
  return { kind, condition, key: targetKey(kind, condition) };
};

/**
 * Where a service shows. The seller's own grid when they saved one; else the
 * template's default; else (a template that no longer exists) everywhere, as
 * before this ticket. An empty saved grid means "nowhere" (the modal does not
 * let a seller save one).
 */
const serviceAppliesTo = service => {
  if (!service) return [];
  if (Array.isArray(service.appliesTo)) {
    return service.appliesTo.filter(key => ALL_TARGETS.includes(key));
  }
  const template = getServiceTemplate(service.templateKey);
  return template ? template.defaultAppliesTo : ALL_TARGETS;
};

/** The unit a new service of this template starts with, and the unit of a
 * stored service that has none. */
const defaultPriceUnitFor = templateKey => {
  const template = getServiceTemplate(templateKey);
  return template ? template.defaultPriceUnit : PRICE_UNIT_ORDER;
};

/**
 * What the "Lägg till tjänst" modal fills in when a type is picked (BR-2):
 * the template's "Visas för" boxes and "Pris gäller". Null for an unknown key.
 */
const templateDefaultsFor = templateKey => {
  const template = getServiceTemplate(templateKey);
  return template
    ? { appliesTo: [...template.defaultAppliesTo], priceUnit: template.defaultPriceUnit }
    : null;
};

/** What the service's price covers: the seller's choice, else the template's. */
const servicePriceUnit = service => {
  if (service && PRICE_UNITS.includes(service.priceUnit)) return service.priceUnit;
  return defaultPriceUnitFor(service && service.templateKey);
};

const isActiveService = service => !!service && !!service.id && !service.isPaused;

/** True when the service is set to show for this listing's type and condition. */
const serviceAppliesToListing = (service, publicData) => {
  const target = listingTarget(publicData);
  return !!target && serviceAppliesTo(service).includes(target.key);
};

/**
 * BR-3 — the seller's services a buyer of this listing is offered: active
 * ones whose "Visas för" covers the listing's type and condition.
 *
 * @param {Array} services the seller's profile.publicData.services
 * @param {Object} publicData the listing's publicData
 * @returns {Array} the services, in the seller's order
 */
const servicesForListing = (services, publicData) =>
  (Array.isArray(services) ? services : []).filter(
    service => isActiveService(service) && serviceAppliesToListing(service, publicData)
  );

/**
 * The services an order of one or more lines is offered: a service shows when
 * it applies to at least one line (a cart may mix tyres and rims).
 *
 * @param {Array} services
 * @param {Array} lines [{ publicData }]
 */
const servicesForOrder = (services, lines) => {
  const list = (Array.isArray(lines) ? lines : []).filter(Boolean);
  return (Array.isArray(services) ? services : []).filter(
    service =>
      isActiveService(service) &&
      list.some(line => serviceAppliesToListing(service, line.publicData))
  );
};

/**
 * BR-5 — the services added without being asked, and locked: montering, on an
 * order with new complete wheels (or a package) in it, when the seller has
 * montering set to show there.
 *
 * @param {Array} services the seller's services
 * @param {Array} lines [{ publicData }]
 * @returns {Array<string>} service ids
 */
const autoServiceIds = (services, lines) => {
  const newWheelLines = (Array.isArray(lines) ? lines : []).filter(line => {
    const target = line && listingTarget(line.publicData);
    return target && target.kind === 'wheel' && target.condition === 'new';
  });
  if (newWheelLines.length === 0) return [];
  return servicesForOrder(services, newWheelLines)
    .filter(service => service.templateKey === MONTERING_TEMPLATE_KEY)
    .slice(0, 1)
    .map(service => service.id);
};

/**
 * The services an order carries: the buyer's choices that apply to it, plus
 * the locked ones (BR-5), in the seller's order. What the server charges and
 * what checkout shows as ticked.
 *
 * @param {Array} services the seller's services
 * @param {Array<string>} selectedServiceIds what the buyer ticked
 * @param {Array} lines [{ publicData }]
 * @returns {Array} service documents
 */
const servicesOnOrder = (services, selectedServiceIds, lines) => {
  const chosen = Array.isArray(selectedServiceIds) ? selectedServiceIds : [];
  const ids = [...chosen, ...autoServiceIds(services, lines)];
  return servicesForOrder(services, lines).filter(service => ids.includes(service.id));
};

const itemsOf = line => {
  const n = Number(line && line.physicalItems);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
};

/**
 * How many wheel positions the service covers in this order: complete wheels
 * + the larger of tyres and rims, among the lines it applies to (see the
 * module comment).
 *
 * @param {Object} service
 * @param {Array} lines [{ publicData, physicalItems }]
 * @returns {number}
 */
const serviceKindCounts = (service, lines) => {
  const counts = { tire: 0, rim: 0, wheel: 0 };
  (Array.isArray(lines) ? lines : []).forEach(line => {
    if (!line || !serviceAppliesToListing(service, line.publicData)) return;
    counts[listingTarget(line.publicData).kind] += itemsOf(line);
  });
  return counts;
};

const serviceUnitCount = (service, lines) => {
  const counts = serviceKindCounts(service, lines);
  return counts.wheel + Math.max(counts.tire, counts.rim);
};

/**
 * "2 däck", "4 hjul", "1 fälg" — the quantity a service row names next to its
 * price (spec §7: "Omläggning · 2 däck · 600 kr"). Tyres alone are däck, rims
 * alone fälgar; anything with a complete wheel in it, or tyres and rims
 * together (which become wheels), is hjul. Null for a price per order, which
 * does not depend on the quantity, and when nothing in the order applies.
 *
 * @param {Object} service
 * @param {Array} lines [{ publicData, physicalItems }]
 * @returns {string|null}
 */
const serviceQuantityLabel = (service, lines) => {
  if (servicePriceUnit(service) === PRICE_UNIT_ORDER) return null;
  const counts = serviceKindCounts(service, lines);
  const count = counts.wheel + Math.max(counts.tire, counts.rim);
  if (count <= 0) return null;
  const noun =
    counts.wheel > 0 || (counts.tire > 0 && counts.rim > 0)
      ? 'hjul'
      : counts.tire > 0
        ? 'däck'
        : count === 1
          ? 'fälg'
          : 'fälgar';
  return `${count} ${noun}`;
};

/**
 * What a price in kronor comes to for `count` items under a unit, in öre.
 * Per sats rounds to whole kronor (BR-7).
 */
const unitSubunits = (priceMajor, unit, count) => {
  const price = Number(priceMajor);
  if (!Number.isFinite(price) || price <= 0 || !(count > 0)) return 0;
  if (unit === PRICE_UNIT_ORDER) return Math.round(price * 100);
  if (unit === PRICE_UNIT_PIECE) return Math.round(price * 100 * count);
  return Math.round((price * count) / SET_SIZE) * 100;
};

/**
 * What one service costs on this order, in öre (BR-7). 0 when it applies to
 * nothing in the order or has no price.
 *
 * @param {Object} service { price (kronor), priceUnit, appliesTo, templateKey }
 * @param {Array} lines [{ publicData, physicalItems }]
 * @returns {number}
 */
const serviceSubunits = (service, lines) =>
  unitSubunits(service && service.price, servicePriceUnit(service), serviceUnitCount(service, lines));

/** "per st", "per 4 st" — the rate's unit, for a price shown before the order
 * size is known. */
const priceUnitSuffix = service => {
  const unit = servicePriceUnit(service);
  if (unit === PRICE_UNIT_PIECE) return 'per st';
  if (unit === PRICE_UNIT_SET4) return 'per 4 st';
  return 'per order';
};

/* ---- Miljöavgift (BR-6) ---- */

const LINE_ITEM_MILJOAVGIFT = 'line-item/miljoavgift';

/* 25 kr per tyre, VAT included (BR-6). THE one place the amount lives. */
const MILJOAVGIFT_PER_TYRE_SUBUNITS = 2500;

/**
 * True when the listing's price already holds the tyre recycling fee.
 *
 * Gummigrossen's own price list carries a RecyclingFee per tyre, and the
 * connector folds it into the listed price (computePrice in
 * server/services/supplierConnectors/gummigrossenMapper.js) — the same fee was
 * once charged twice on the shop page for exactly this reason. Those listings
 * are marked by their supplier metadata: `supplierFeed` on a feed import,
 * `supplierRef` on a listing made for one shop order. `recyclingFeeIncluded`
 * is the explicit flag for any later source that does the same.
 */
const priceIncludesRecyclingFee = (metadata = {}) => {
  if (!metadata) return false;
  if (metadata.recyclingFeeIncluded === true) return true;
  if (metadata.supplierFeed && metadata.supplierFeed.source === 'gummigrossen') return true;
  return /^gummigrossen:/.test(String(metadata.supplierRef || ''));
};

/**
 * How many tyres in the order pay the miljöavgift: one per item on every NEW
 * tyre, complete-wheel or package line whose price does not already hold it.
 * Never on used goods or rims.
 *
 * @param {Array} lines [{ publicData, metadata, physicalItems }]
 * @returns {number}
 */
const miljoavgiftTyres = lines =>
  (Array.isArray(lines) ? lines : []).reduce((sum, line) => {
    const target = line && listingTarget(line.publicData);
    if (!target || target.condition !== 'new' || target.kind === 'rim') return sum;
    if (priceIncludesRecyclingFee(line.metadata)) return sum;
    return sum + itemsOf(line);
  }, 0);

/** The miljöavgift of the whole order, in öre. */
const miljoavgiftSubunits = lines => miljoavgiftTyres(lines) * MILJOAVGIFT_PER_TYRE_SUBUNITS;

/** "Miljöavgift 4 × 25 kr" — the row label everywhere the fee is shown. */
const miljoavgiftLabel = tyres =>
  `Miljöavgift ${tyres} × ${MILJOAVGIFT_PER_TYRE_SUBUNITS / 100} kr`;

module.exports = {
  PRODUCT_KINDS,
  CONDITIONS,
  PRODUCT_KIND_LABELS,
  CONDITION_LABELS,
  ALL_TARGETS,
  PRICE_UNIT_PIECE,
  PRICE_UNIT_SET4,
  PRICE_UNIT_ORDER,
  PRICE_UNITS,
  PRICE_UNIT_LABELS,
  SET_SIZE,
  SERVICE_TEMPLATES,
  MONTERING_TEMPLATE_KEY,
  MONTERING_INCLUDED_TEXT,
  LINE_ITEM_MILJOAVGIFT,
  MILJOAVGIFT_PER_TYRE_SUBUNITS,
  targetKey,
  getServiceTemplate,
  getServiceTemplateLabel,
  listingTarget,
  serviceAppliesTo,
  servicePriceUnit,
  defaultPriceUnitFor,
  templateDefaultsFor,
  serviceAppliesToListing,
  servicesForListing,
  servicesForOrder,
  autoServiceIds,
  servicesOnOrder,
  serviceUnitCount,
  serviceQuantityLabel,
  unitSubunits,
  serviceSubunits,
  priceUnitSuffix,
  priceIncludesRecyclingFee,
  miljoavgiftTyres,
  miljoavgiftSubunits,
  miljoavgiftLabel,
};
